import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Form,
  InputGroup,
  Modal,
  Spinner,
} from 'react-bootstrap';
import { Search, TelephoneFill } from 'react-bootstrap-icons';
import axios from '../axiosAlertConfig';

const cleanText = value => {
  if (value === null || value === undefined) return '';
  const text = String(value).trim();
  return /^(null|undefined)$/i.test(text) ? '' : text;
};

const cleanPhone = value => {
  if (value && typeof value === 'object') {
    return cleanText(
      value.senderMobileNumber ||
      value.phoneNumber ||
      value.mobileNumber ||
      value.phone ||
      '',
    );
  }

  return cleanText(value);
};

const normalizePhoneKey = value => {
  const phone = cleanPhone(value);
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  return digits || phone.toLowerCase();
};

const toTelHref = value => {
  const phone = cleanPhone(value);
  if (!phone) return '';

  const prefix = phone.startsWith('+') ? '+' : '';
  const digits = phone.replace(/\D/g, '');
  return digits ? `tel:${prefix}${digits}` : '';
};

const parseStatus = raw => {
  let value = raw;

  for (let attempt = 0; attempt < 3; attempt += 1) {
    if (Array.isArray(value)) {
      return value.map(item => cleanText(item)).filter(Boolean);
    }

    if (value === null || value === undefined || value === '') return [];

    if (typeof value !== 'string') {
      return [cleanText(value)].filter(Boolean);
    }

    const trimmed = value.trim();
    if (!trimmed) return [];

    try {
      value = JSON.parse(trimmed);
    } catch {
      return [trimmed];
    }
  }

  return Array.isArray(value)
    ? value.map(item => cleanText(item)).filter(Boolean)
    : [cleanText(value)].filter(Boolean);
};

const formatDateTime = value => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? cleanText(value) : date.toLocaleString();
};

const accountKey = row => cleanText(row?.iAccountID);
const userKey = row => {
  const userID = cleanText(row?.senderUserID);
  return userID ? `${accountKey(row)}::${userID}` : '';
};
const nameKey = row => {
  const name = cleanText(row?.senderName).toLowerCase();
  return name ? `${accountKey(row)}::${name}` : '';
};

function addSenderKeys(messages) {
  const userKeysByPhone = new Map();
  const userKeysByName = new Map();

  messages.forEach(row => {
    const byUser = userKey(row);
    if (!byUser) return;

    const canonicalUserKey = `user:${byUser}`;
    const phoneKey = normalizePhoneKey(row);
    const byName = nameKey(row);

    if (phoneKey) {
      const userCandidates = userKeysByPhone.get(phoneKey) || new Set();
      userCandidates.add(canonicalUserKey);
      userKeysByPhone.set(phoneKey, userCandidates);
    }

    if (byName) {
      const userCandidates = userKeysByName.get(byName) || new Set();
      userCandidates.add(canonicalUserKey);
      userKeysByName.set(byName, userCandidates);
    }
  });

  return messages.map((row, index) => {
    const byUser = userKey(row);
    const phoneKey = normalizePhoneKey(row);
    const byName = nameKey(row);
    const phoneUserCandidates = phoneKey ? userKeysByPhone.get(phoneKey) : null;
    const nameUserCandidates = byName ? userKeysByName.get(byName) : null;

    let senderKey = byUser ? `user:${byUser}` : '';
    if (!senderKey && phoneUserCandidates?.size === 1) {
      senderKey = phoneUserCandidates.values().next().value;
    }
    if (!senderKey && nameUserCandidates?.size === 1) {
      senderKey = nameUserCandidates.values().next().value;
    }
    if (!senderKey && phoneKey) {
      senderKey = `phone:${accountKey(row)}::${phoneKey}`;
    }
    if (!senderKey && byName) senderKey = `name:${byName}`;
    if (!senderKey) senderKey = `message:${row?.messageID ?? index}`;

    return {
      row,
      index,
      senderKey,
      messageKey: cleanText(row?.messageID) || `index-${index}`,
    };
  });
}

const searchTextForMessage = row =>
  [
    row?.messageText,
    row?.senderName,
    cleanPhone(row),
    row?.myLocation,
    row?.street_address,
    row?.attachmentType,
    row?.firstName,
    row?.lastName,
    formatDateTime(row?.createdAt),
    ...parseStatus(row?.myStatus),
  ]
    .map(cleanText)
    .filter(Boolean)
    .join('\n')
    .toLocaleLowerCase();

const plural = (count, singular, pluralValue = `${singular}s`) =>
  `${count} ${count === 1 ? singular : pluralValue}`;

export default function IncidentMessageSearchModal({
  show,
  onHide,
  incidentID,
  incidentLabel = '',
}) {
  const [messages, setMessages] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!show) return undefined;

    setQuery('');
    setMessages([]);
    setError('');
    setLoading(false);

    if (!incidentID) return undefined;

    const controller = new AbortController();

    const loadMessages = async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(
          `/ialert/incidentmessages/${incidentID}`,
          { signal: controller.signal },
        );

        if (!data?.success) {
          throw new Error(data?.message || 'Unable to load incident messages.');
        }

        setMessages(Array.isArray(data.messages) ? data.messages : []);
      } catch (err) {
        if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') return;
        console.error('load searchable incident messages', err);
        setError(
          err?.response?.data?.message ||
          err?.message ||
          'Unable to load incident messages.',
        );
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    loadMessages();
    return () => controller.abort();
  }, [show, incidentID]);

  const searchResults = useMemo(() => {
    const term = query.trim().toLocaleLowerCase();
    if (!term) {
      return {
        groups: [],
        matchingMessageCount: 0,
        totalContextMessageCount: 0,
      };
    }

    const keyedMessages = addSenderKeys(messages);
    const matchedMessageKeys = new Set();
    const matchedSenderKeys = new Set();

    keyedMessages.forEach(item => {
      if (searchTextForMessage(item.row).includes(term)) {
        matchedMessageKeys.add(item.messageKey);
        matchedSenderKeys.add(item.senderKey);
      }
    });

    const groupsBySender = new Map();

    keyedMessages.forEach(item => {
      if (!matchedSenderKeys.has(item.senderKey)) return;

      const existing = groupsBySender.get(item.senderKey) || {
        senderKey: item.senderKey,
        senderName: '',
        phone: '',
        messages: [],
        matchingMessageCount: 0,
        latestMatchTime: 0,
      };

      const isMatch = matchedMessageKeys.has(item.messageKey);
      const messageTime = new Date(item.row?.createdAt || 0).getTime() || 0;

      if (!existing.senderName) {
        existing.senderName = cleanText(item.row?.senderName) || 'Unknown sender';
      }
      if (!existing.phone) existing.phone = cleanPhone(item.row);
      if (isMatch) {
        existing.matchingMessageCount += 1;
        existing.latestMatchTime = Math.max(existing.latestMatchTime, messageTime);
      }

      existing.messages.push({
        ...item,
        isMatch,
        messageTime,
      });

      groupsBySender.set(item.senderKey, existing);
    });

    const groups = [...groupsBySender.values()]
      .map(group => ({
        ...group,
        messages: [...group.messages].sort(
          (a, b) => a.messageTime - b.messageTime || a.index - b.index,
        ),
      }))
      .sort(
        (a, b) =>
          b.latestMatchTime - a.latestMatchTime ||
          a.senderName.localeCompare(b.senderName),
      );

    return {
      groups,
      matchingMessageCount: matchedMessageKeys.size,
      totalContextMessageCount: groups.reduce(
        (total, group) => total + group.messages.length,
        0,
      ),
    };
  }, [messages, query]);

  const trimmedQuery = query.trim();

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      scrollable
      backdrop="static"
    >
      <Modal.Header closeButton>
        <div className="w-100 me-3">
          <Modal.Title>Search Incident Messages</Modal.Title>
          {incidentLabel && (
            <div className="small text-muted mt-1">{incidentLabel}</div>
          )}
        </div>
      </Modal.Header>

      <Modal.Body>
        <InputGroup className="mb-2">
          <InputGroup.Text aria-hidden="true">
            <Search />
          </InputGroup.Text>
          <Form.Control
            autoFocus
            value={query}
            onChange={event => setQuery(event.target.value)}
            placeholder="Search message text, person, phone, status, location, or date…"
            aria-label="Search all messages for this incident"
            disabled={!!error}
          />
          {query && (
            <Button
              variant="outline-secondary"
              onClick={() => setQuery('')}
            >
              Clear
            </Button>
          )}
        </InputGroup>

        <div className="small text-muted mb-3">
          A match returns every message from the matching person so the full conversation remains visible. Matching messages are highlighted.
        </div>

        {loading && (
          <div className="d-flex align-items-center justify-content-center gap-2 py-5">
            <Spinner animation="border" size="sm" />
            <span>Loading messages…</span>
          </div>
        )}

        {!loading && error && <Alert variant="danger">{error}</Alert>}

        {!loading && !error && !trimmedQuery && (
          <div className="text-center text-muted py-5">
            Enter search text to find messages and their conversational context.
          </div>
        )}

        {!loading && !error && trimmedQuery && searchResults.groups.length === 0 && (
          <Alert variant="secondary" className="mb-0">
            No incident messages match “{trimmedQuery}”.
          </Alert>
        )}

        {!loading && !error && searchResults.groups.length > 0 && (
          <>
            <div className="small mb-3">
              <strong>
                {plural(searchResults.matchingMessageCount, 'matching message')}
              </strong>{' '}
              from {plural(searchResults.groups.length, 'person', 'people')}; showing{' '}
              {plural(searchResults.totalContextMessageCount, 'message')} for context.
            </div>

            {searchResults.groups.map(group => {
              const groupTelHref = toTelHref(group.phone);

              return (
                <section
                  key={group.senderKey}
                  className="border rounded mb-3 overflow-hidden"
                >
                  <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 bg-light border-bottom p-2">
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <strong>{group.senderName}</strong>
                      {groupTelHref && (
                        <a
                          href={groupTelHref}
                          className="text-decoration-none d-inline-flex align-items-center gap-1"
                          title={`Call ${group.phone}`}
                          aria-label={`Call ${group.senderName} at ${group.phone}`}
                        >
                          <TelephoneFill size={13} />
                          {group.phone}
                        </a>
                      )}
                    </div>

                    <div className="d-flex gap-2">
                      <Badge bg="warning" text="dark">
                        {plural(group.matchingMessageCount, 'match', 'matches')}
                      </Badge>
                      <Badge bg="secondary">
                        {plural(group.messages.length, 'message')}
                      </Badge>
                    </div>
                  </div>

                  <div className="p-2">
                    {group.messages.map(item => {
                      const row = item.row;
                      const phone = cleanPhone(row) || group.phone;
                      const telHref = toTelHref(phone);
                      const statuses = parseStatus(row?.myStatus);
                      const location = cleanText(row?.myLocation || row?.street_address);
                      const messageText = cleanText(row?.messageText) ||
                        (row?.attachmentURL ? cleanText(row?.attachmentType) || 'Attachment' : '');

                      return (
                        <article
                          key={`${group.senderKey}-${item.messageKey}`}
                          className="rounded border p-2 mb-2"
                          style={{
                            background: item.isMatch ? '#fff3cd' : '#fff',
                            borderLeft: item.isMatch
                              ? '4px solid #ffc107'
                              : '4px solid transparent',
                          }}
                        >
                          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-1">
                            <div className="d-flex flex-wrap align-items-center gap-2">
                              <span className="fw-semibold d-inline-flex align-items-center gap-1">
                                {telHref && (
                                  <a
                                    href={telHref}
                                    className="text-decoration-none"
                                    title={`Call ${phone}`}
                                    aria-label={`Call ${cleanText(row?.senderName) || group.senderName} at ${phone}`}
                                  >
                                    <TelephoneFill size={12} />
                                  </a>
                                )}
                                {cleanText(row?.senderName) || group.senderName}
                              </span>

                              {item.isMatch && (
                                <Badge bg="warning" text="dark">Match</Badge>
                              )}
                            </div>

                            <small className="text-muted">
                              {formatDateTime(row?.createdAt)}
                            </small>
                          </div>

                          {messageText && (
                            <div style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                              {messageText}
                            </div>
                          )}

                          {row?.attachmentURL && (
                            <div className="mt-1">
                              <a
                                href={row.attachmentURL}
                                target="_blank"
                                rel="noreferrer"
                              >
                                Open {cleanText(row?.attachmentType) || 'attachment'}
                              </a>
                            </div>
                          )}

                          {(location || statuses.length > 0) && (
                            <div className="d-flex flex-wrap gap-2 mt-2">
                              {location && <Badge bg="secondary">{location}</Badge>}
                              {statuses.map((status, statusIndex) => (
                                <Badge
                                  key={`${status}-${statusIndex}`}
                                  bg="info"
                                  text="dark"
                                >
                                  {status}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </article>
                      );
                    })}
                  </div>
                </section>
              );
            })}
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
}
